import { useCallback, useContext, createContext, useEffect, useRef, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useDocument } from "react-firebase-hooks/firestore";
import { doc } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { auth, firestore } from "hooks/useFirebase";
import { useAppStateContext } from "hooks/useAppState/useAppState";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logAuth, LogLevel, setUser } from "utils/sentryLogger";
import { constants } from "utils/general";
import { identifyTeacher, trackLogin, PENDING_LOGIN_METHOD_KEY } from "utils/analytics";

const loggedAuthEvents = new Set();
export const resetLoggedAuthEvents = () => loggedAuthEvents.clear();

const getAuthEventKey = (userId, eventType) => `${userId || 'anonymous'}_${eventType}`;

// The only pages that run without a login: the group-code entry (and its
// timeout variant) and the session itself. Everywhere else a missing Firebase
// user is a teacher whose session is still restoring or has dropped, and
// creating an anonymous user there would replace that session for real.
export const isStudentPath = (pathname) =>
  pathname === "/" || pathname === "/timeout" || pathname.startsWith("/session/");

export const getAuthMethod = (authUser, sessionAuthType) => {
  if (sessionAuthType) return sessionAuthType;
  if (authUser?.isAnonymous) return constants.ANONYMOUS;
  if (authUser) return "oauth";
  return constants.ANONYMOUS;
};

export const useAuthUserData = () => {
  const [authUser, authLoading, authError] = useAuthState(auth);
  const [docUser, docLoading, docError] = useDocument(
    authUser?.uid ? doc(firestore, `users/${authUser?.uid}`) : null
  );
  const { sessionAuthType } = useAppStateContext();
  const [okoUserId, setOkoUserIdState] = useState(null);

  // Manual setter — called from Session.js after group/{code}/join resolves so
  // students pick up their OKO SQL id as soon as the backend returns it.
  const setOkoUserId = useCallback((id) => {
    if (id == null) return;
    setOkoUserIdState(String(id));
  }, []);

  // Educators: fetch OKO SQL id once per non-anonymous Firebase user.
  // The endpoint 404s for non-educator OAuth users — we swallow and stay null.
  const educatorFetchedForUidRef = useRef(null);
  useEffect(() => {
    if (authLoading) return;
    if (!authUser || authUser.isAnonymous) {
      if (educatorFetchedForUidRef.current !== null) {
        educatorFetchedForUidRef.current = null;
        setOkoUserIdState(null);
      }
      return;
    }
    if (educatorFetchedForUidRef.current === authUser.uid) return;
    educatorFetchedForUidRef.current = authUser.uid;

    request("account/educator", { method: "GET" })
      .then((data) => {
        if (data?.oko_user_id != null) {
          const id = String(data.oko_user_id);
          setOkoUserIdState(id);
          identifyTeacher({
            okoUserId: id,
            email: authUser.email,
            name: authUser.displayName,
            schoolName: data?.active_school?.name,
            client: data?.client_tier,
            config: data?.config,
          });
          // "Login" means a completed sign-in: every sign-in path leaves its
          // provider in sessionStorage, and a reload, a new tab or a persisted
          // session leaves nothing. Consuming it here makes it count once.
          let pendingMethod = null;
          try {
            pendingMethod = sessionStorage.getItem(PENDING_LOGIN_METHOD_KEY);
            if (pendingMethod) sessionStorage.removeItem(PENDING_LOGIN_METHOD_KEY);
          } catch (_) {
            // sessionStorage unavailable — fall through.
          }
          if (pendingMethod) {
            trackLogin({
              method: pendingMethod,
              userId: id,
              email: authUser.email,
            });
          }
        }
      })
      .catch(() => {
        // Not an educator, endpoint down, etc. — leave okoUserId null.
      });
  }, [authUser, authLoading]);

  // Keep Sentry scope in sync so out-of-band captures (uncaught errors, etc.)
  // carry the same identity as our structured logs.
  useEffect(() => {
    if (!authUser) {
      setUser(null);
      return;
    }
    setUser({ okoUserId, firebaseUid: authUser.uid });
  }, [authUser, okoUserId]);

  useEffect(() => {
    // Don't process while still loading
    if (authLoading) {
      return;
    }

    // Skip if user is already authenticated
    if (authUser) {
      const eventKey = getAuthEventKey(authUser.uid, 'skip_authenticated');
      if (!loggedAuthEvents.has(eventKey)) {
        logAuth("Anonymous auth skipped", {
          authMethod: getAuthMethod(authUser, sessionAuthType),
          reason: "user_already_authenticated",
          userId: authUser.uid,
        });
        loggedAuthEvents.add(eventKey);
      }
      return;
    }

    if (!isStudentPath(window.location.pathname)) {
      const eventKey = getAuthEventKey(null, "skip_educator_route");
      if (!loggedAuthEvents.has(eventKey)) {
        logAuth("Anonymous auth skipped", {
          authMethod: constants.ANONYMOUS,
          reason: "educator_route",
          pathname: window.location.pathname,
        });
        loggedAuthEvents.add(eventKey);
      }
      return;
    }

    // Don't sign in anonymously if an OAuth session is active (user just completed OAuth)
    // This prevents overwriting OAuth authentication with anonymous authentication.
    // Anonymous sessionAuthType still needs sign-in (e.g. after page refresh rehydration).
    if (sessionAuthType && sessionAuthType !== constants.ANONYMOUS) {
      const eventKey = getAuthEventKey(null, `skip_oauth_${sessionAuthType}`);
      if (!loggedAuthEvents.has(eventKey)) {
        logAuth("Anonymous auth skipped", {
          authMethod: sessionAuthType,
          reason: "oauth_session_active",
        });
        loggedAuthEvents.add(eventKey);
      }
      return;
    }

    // Check if we've already initiated anonymous auth this session
    const initiatedKey = getAuthEventKey(null, 'anonymous_initiated');
    if (loggedAuthEvents.has(initiatedKey)) {
      return;
    }
    loggedAuthEvents.add(initiatedKey);

    // Log anonymous auth initiation
    logAuth("Anonymous auth initiated", {
      authMethod: constants.ANONYMOUS,
      reason: "no_existing_auth",
    });

    signInAnonymously(auth)
      .then(() => {
        logAuth("Anonymous auth success", {
          authMethod: constants.ANONYMOUS,
        });
      })
      .catch(e => {
        console.warn("Error signing in anonymously:", e);
        logAuth("Anonymous auth failed", {
          level: LogLevel.WARNING,
          authMethod: constants.ANONYMOUS,
          errorMessage: e.message,
        });
      });
  }, [authLoading, authUser, sessionAuthType]);

  // With no Firebase user the user document has a null ref and useDocument
  // hands back undefined forever. On a student route that is fine — anonymous
  // sign-in is about to create the user — but on an educator route that
  // sign-in is skipped, so "no user" is the final state and must resolve, or
  // WaitForUser blanks the whole app, login page included.
  const noUserIsFinal = !authLoading && !authUser && !isStudentPath(window.location.pathname);
  let loading = !noUserIsFinal && (authLoading || docLoading || docUser === undefined);
  const error = authError || docError;

  if (error) loading = false;

  const docUserData = docUser?.data();
  const authUserData = error || loading ? null : {
    id: authUser?.uid,
    emailVerified: authUser?.emailVerified,
    // No user reads as anonymous: that is what every "not logged in →
    // login page" check keys on, and getAuthMethod already says the same.
    isAnonymous: !authUser || authUser.isAnonymous,
    displayName: authUser?.displayName || docUserData?.displayName,
    email: authUser?.email,
    okoUserId,
  };
  return { authUserData, loading, error, setOkoUserId };
};

const Context = createContext();

export const AuthUserDataProvider = ({ children }) => {
  const value = useAuthUserData();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useAuthUserDataContext = () => {
  return useContext(Context);
};
